import { Injectable } from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { PersistenciaService } from '../persistencia-movimientos/persistencia.service';

/**
 * Interfaz base para el objeto de auditoría.
 * Todos los métodos retornan al menos estos campos.
 */
export interface AuditBase {
  comentarios: string;
  origen: string;
  user: string;
}

/**
 * Objeto de auditoría con información de documento y/o matchkey.
 */
export interface AuditWithDocument extends AuditBase {
  document?: string;
  // matchkey puede ser string o array de strings para soportar operaciones masivas
  matchkey?: string | string[];
  fields?: Record<string, any>;
}

/**
 * Objeto de auditoría para operaciones DIS (usa claves en inglés: comments, origin).
 */
export interface AuditDis {
  comments: string;
  origin: string;
  user: string;
  // matchkey puede ser string o array de strings para soportar operaciones masivas
  matchkey?: string | string[];
  document?: string;
  fields?: Record<string, any>;
}

/**
 * Opciones adicionales para construir el objeto de auditoría.
 */
export interface AuditOptions {
  /** Comentario personalizado (sobreescribe el del localStorage) */
  customComment?: string;
  /** Documento de la persona */
  document?: string;
  /** Matchkey del registro o array de matchkeys en caso de masivo */
  matchkey?: string | string[];
  /** Campos modificados ya calculados */
  fields?: Record<string, any>;
  /** Si es true, no limpia el comentario del localStorage */
  keepComment?: boolean;
}

/**
 * Servicio centralizado para la construcción de objetos de auditoría.
 *
 * Reemplaza los métodos duplicados:
 *  - buildAuditObjectForMerge
 *  - buildAuditObjectForDissociate
 *  - buildAuditObjectForBulk
 *  - buildAuditObjectForFintech
 *  - buildAuditObjectForFintechMassiveUpdate
 *  - buildAuditObject (fuente-dinamica-detail)
 *  - buildAuditObjectForUyBasePositiva / Overdraft / Loan / CreditCard
 *  - buildAuditObjectForMassiveBcraUpdate
 *  - buildAuditObjectForMassiveTradeUpdate
 *  - buildAuditObjectForMassiveUpdateSicomMicro
 *  - buildAuditObjectForDis
 *  - buildAuditObject (double-preview)
 *
 * Uso básico:
 *   const audit = this.auditService.buildAuditBase();
 *   const audit = this.auditService.buildAudit({ document: '12345', matchkey: 'mk', fields: {...} });
 *   const audit = this.auditService.buildAuditDis({ comments: 'Edición Masiva', fields: {...} });
 */
@Injectable({
  providedIn: 'root'
})
export class AuditService {

  constructor(
    private localStorageService: LocalStorageService,
    private persistenciaService: PersistenciaService
  ) {}

  // ───────────────────────────��─────────────────
  //  Métodos principales
  // ─────────────────────────────────────────────

  /**
   * Calcula el origen de la auditoría:
   *  - 'RDS' si el contexto actual es RDS (independientemente de si es VIP o DM)
   *  - 'VIP' si es VIP (y no es RDS)
   *  - 'DM'  en cualquier otro caso
   */
  private getOrigen(): string {
    if (this.localStorageService.isMenuRds()) {
      return 'RDS';
    }
    return this.localStorageService.getIsVip ? 'VIP' : 'DM';
  }

  /**
   * Construye el objeto de auditoría base (comentarios, origen, user).
   * Equivale a lo que hacía buildAuditObjectForMerge.
   */
  buildAuditBase(options?: AuditOptions): AuditBase {
    const user = this.localStorageService.dataLogin?.getUser();
    const comentarios = options?.customComment ?? this.localStorageService.comment;
    const origen = this.getOrigen();

    if (!options?.keepComment) {
      this.localStorageService.comment = '';
    }

    return { comentarios, origen, user };
  }

  /**
   * Construye el objeto de auditoría con documento, matchkey y campos opcionales.
   * Reemplaza: buildAuditObjectForDissociate, buildAuditObjectForFintech,
   *            buildAuditObjectForUyBasePositiva, buildAuditObject (fuente-dinamica-detail),
   *            buildAuditObject (double-preview), etc.
   */
  buildAudit(options?: AuditOptions): AuditWithDocument {
    const base = this.buildAuditBase(options);

    const result: AuditWithDocument = { ...base };

    if (options?.document !== undefined) {
      result.document = options.document;
    }
    if (options?.matchkey !== undefined) {
      // mantener el tipo: string o string[] según venga
      result.matchkey = options.matchkey;
    }
    if (options?.fields !== undefined) {
      result.fields = options.fields;
    }

    return result;
  }

  /**
   * Construye el objeto de auditoría en formato DIS (claves en inglés: comments, origin).
   * Reemplaza: buildAuditObjectForDis, buildAuditObjectForMassiveUpdateSicomMicro.
   */
  buildAuditDis(options?: AuditOptions): AuditDis {
    const user = this.localStorageService.dataLogin?.getUser();
    const comments = options?.customComment ?? this.localStorageService.comment;
    const origin = this.getOrigen();

    if (!options?.keepComment) {
      this.localStorageService.comment = '';
    }

    const result: AuditDis = { comments, origin, user };

    if (options?.document !== undefined) {
      result.document = options.document;
    }
    if (options?.matchkey !== undefined) {
      // conservar string o array según corresponda
      result.matchkey = options.matchkey;
    }
    if (options?.fields !== undefined) {
      result.fields = options.fields;
    }

    return result;
  }

  // ─────────────────────────────────────────────
  //  Métodos de conveniencia para casos específicos
  // ─────────────────────────────────────────────

  /**
   * Construye auditoría para Bulk.
   * Reemplaza: buildAuditObjectForBulk en bulk.component.ts
   */
  buildAuditForBulk(jsonBulk: any): AuditWithDocument {
    const comentarios = jsonBulk.internalOp === 'BULK_DELETE' && jsonBulk.deletionReason
      ? `Motivo de la eliminación: ${jsonBulk.deletionReason}`
      : `Operación Bulk: ${jsonBulk.internalOp}${jsonBulk.deletionReason ? ` - ${jsonBulk.deletionReason}` : ''}`;

    let fields: Record<string, any> = {};
    if (jsonBulk.internalOp === 'BULK_UPDATE') {
      fields = jsonBulk.changeDetails.reduce((acc: any, change: any) => {
        acc[change.fieldToChange] = { after: change.newValue, before: null };
        return acc;
      }, {});
    } else {
      fields = {
        operation: 'BULK_DELETE',
        criteria: jsonBulk.criterios
      };
    }

    return this.buildAudit({ customComment: comentarios, fields });
  }

  /**
   * Construye auditoría para edición/creación de componentes con formulario (Fintech, UY Base Positiva, etc.).
   * Reemplaza: buildAuditObjectForFintech, buildAuditObjectForUyBasePositiva,
   *            buildAuditObjectForOverdraft, buildAuditObjectForLoan, buildAuditObjectForCreditCard.
   *
   * @param isCreating   Si es true, no calcula campos modificados (fields queda vacío).
   * @param document     Documento de la persona.
   * @param matchkey     Matchkey del registro.
   * @param changeDetails  Array de cambios (output de sharedService.createUpdateObject().changeDetails).
   * @param currentPeriodo Periodo actual (para persistenciaService.fieldsModifiedManual).
   */
  buildAuditForDetail(
    isCreating: boolean,
    document: string,
    matchkey: string,
    changeDetails?: any[],
    currentPeriodo?: any
  ): AuditWithDocument {
    let fields: Record<string, any> = {};
    if (!isCreating && changeDetails) {
      fields = this.persistenciaService.fieldsModifiedManual(changeDetails, currentPeriodo);
    }

    return this.buildAudit({ document, matchkey, fields });
  }

  /**
   * Construye auditoría para fuente dinámica detail (modo RDS vs normal).
   * Reemplaza: buildAuditObject en fuente-dinamica-detail.component.ts
   *
   * @param country  País.
   * @param isRds    Si es true, retorna solo la base (sin matchkey/document/fields).
   * @param matchkey Matchkey del registro seleccionado.
   * @param fuenteData      Datos originales de la fuente (para getModifiedFields).
   * @param cambiosGFD      Cambios del GFD (FormGroup del localStorage).
   */
  buildAuditForFuenteDinamica(
    country: string,
    isRds: boolean,
    matchkey?: string,
    fuenteData?: any,
    cambiosGFD?: any
  ): AuditWithDocument {
    if (isRds) {
      return this.buildAudit({});
    }

    const document = this.persistenciaService.getvalidaDocumentoPersona(country);
    const fields = this.persistenciaService.getModifiedFields(fuenteData, cambiosGFD);

    return this.buildAudit({ document, matchkey, fields });
  }

  /**
   * Construye auditoría para edición masiva (BCRA).
   * Reemplaza: buildAuditObjectForMassiveBcraUpdate en bcra.service.ts
   */
  buildAuditForMassiveBcra(
    representativeItem: any,
    res: any,
    currentHistoryMatchKey: string
  ): AuditWithDocument {
    const comentarios = res.comentarios || this.localStorageService.comment || 'Edición masiva de registros de BCRA.';
    const document = representativeItem.documento || representativeItem.nationalId || representativeItem.doc_nro;
    const matchkey = currentHistoryMatchKey;

    const fields: Record<string, any> = {};
    if (res.value) {
      fields['situacion_cod'] = { after: res.value };
    }
    if (res.estado) {
      fields['motivo'] = { after: res.estado };
    }
    if (res.periodoini) {
      fields['periodo_inicio'] = { after: res.periodoini };
    }
    if (res.periodofin) {
      fields['periodo_fin'] = { after: res.periodofin };
    }

    return this.buildAudit({ customComment: comentarios, document, matchkey, fields });
  }

  /**
   * Construye auditoría para edición masiva de Trade (RD).
   * Reemplaza: buildAuditObjectForMassiveTradeUpdate en trade.service.ts
   */
  buildAuditForMassiveTrade(
    representativeItem: any,
    edits: any,
    currentHistoryMatchKey: string
  ): AuditWithDocument {
    const comentarios = this.localStorageService.comment || 'Edición masiva de registros de Trade.';
    const document = representativeItem.nationalId;
    const matchkey = currentHistoryMatchKey;

    const fields: Record<string, any> = {};
    if (edits.balancesCero) {
      fields['balances_to_zero'] = { after: 'Sí' };
    }
    if (edits.contadoresCero) {
      fields['counters_to_zero'] = { after: 'Sí' };
    }

    const detailEdits = edits.detailedEdits || {};
    Object.keys(detailEdits).forEach(key => {
      fields[key] = { after: detailEdits[key] };
    });

    return this.buildAudit({ customComment: comentarios, document, matchkey, fields });
  }

  /**
   * Construye auditoría para eliminación masiva SICOM/Microfinance.
   * Reemplaza: buildAuditObjectForMassiveUpdateSicomMicro en fuente-dinamica.component.ts
   */
  buildAuditForMassiveSicomMicro(
    representativeItem: any,
    // currentHistoryMatchKey puede ser string o array de strings
    currentHistoryMatchKey: string | string[],
    subDomainJO?: string
  ): AuditDis {
    const comments = this.localStorageService.comment || `Eliminación masiva ${subDomainJO || ''}`;
    const document = representativeItem.identifierNumber;
    const matchkey = currentHistoryMatchKey;

    return this.buildAuditDis({ customComment: comments, document, matchkey, fields: {} });
  }

  /**
   * Construye auditoría para edición masiva DIS.
   * Reemplaza: buildAuditObjectForDis en fuente-dinamica.component.ts
   */
  buildAuditForMassiveDis(comments: string, fieldsUpdated: any, matchkey?: string | string[]): AuditDis {
    const auditFields: Record<string, any> = {};
    for (const key in fieldsUpdated) {
      if (Object.prototype.hasOwnProperty.call(fieldsUpdated, key)) {
        auditFields[key] = { after: fieldsUpdated[key] };
      }
    }

    return this.buildAuditDis({ customComment: comments, fields: auditFields, matchkey });
  }

  /**
   * Construye auditoría para el double-preview service.
   * Reemplaza: buildAuditObject en double-preview.service.ts
   */
  buildAuditForDoublePreview(originalObject: any, form: any): AuditWithDocument {
    const document = originalObject.identifierNumber;
    const matchkey = originalObject.matchKey;
    const fields = this.persistenciaService.getModifiedFields(originalObject, form);

    return this.buildAudit({ document, matchkey, fields });
  }

  /**
   * Construye auditoría para Fintech edición masiva.
   * Reemplaza: buildAuditObjectForFintechMassiveUpdate en fintech-detail.component.ts
   */
  buildAuditForFintechMassive(params: any, country: string): AuditWithDocument {
    const document = this.persistenciaService.getvalidaDocumentoPersona(country);
    // conservar comportamiento previo (puede ser string o string[] según set en localStorage)
    const matchkey = this.localStorageService.getMatchkeySelected;

    return this.buildAudit({
      customComment: params.comentarios,
      document,
      matchkey,
      fields: {}
    });
  }
}

